// client/src/components/search/SearchBar.js
import React, { useState, useCallback } from 'react';

const SearchBar = ({
  searchTerm,
  onSearchChange,
  onClear,
  onSubmit,
  suggestions,
  showSuggestions,
  isSearching,
  onSuggestionSelect,
  activeFilterCount,
  onFilterClick,
  searchInputRef,
  suggestionsRef
}) => {
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  const handleKeyDown = useCallback((e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestionIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestionIndex(prev =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
      } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
        e.preventDefault();
        onSuggestionSelect(suggestions[activeSuggestionIndex]);
        setActiveSuggestionIndex(-1);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        setActiveSuggestionIndex(-1);
        if (onSubmit) onSubmit();
      } else if (e.key === 'Escape') {
        onClear();
        setActiveSuggestionIndex(-1);
      }
    } else if (e.key === 'Enter' && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  }, [showSuggestions, suggestions, activeSuggestionIndex, onSuggestionSelect, onClear, onSubmit]);

  const handleChange = (value) => {
    setActiveSuggestionIndex(-1);
    onSearchChange(value);
  };

  return (
    <div className="mobile-search-wrapper" role="search">
      <div className="mobile-search-input-container" ref={searchInputRef}>
        <i className="fas fa-search mobile-search-icon" aria-hidden="true"></i>
        <input
          type="text"
          className="mobile-search-input"
          placeholder="Aktie suchen..."
          aria-label="Aktie suchen"
          aria-autocomplete="list"
          aria-controls={showSuggestions && suggestions.length > 0 ? 'search-suggestions' : undefined}
          aria-activedescendant={activeSuggestionIndex >= 0 ? `suggestion-${suggestions[activeSuggestionIndex]?.id}` : undefined}
          aria-expanded={showSuggestions && suggestions.length > 0}
          role="combobox"
          value={searchTerm}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {isSearching && (
          <div className="search-input-spinner" role="status" aria-label="Suche läuft"></div>
        )}
        {searchTerm && (
          <button
            type="button"
            className="clear-search-btn"
            onClick={onClear}
            aria-label="Suchfeld leeren"
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        )}
      </div>

      {/* Filter Button */}
      <button
        type="button"
        className="mobile-filter-btn"
        onClick={onFilterClick}
        aria-label={`Filter öffnen${activeFilterCount > 0 ? `, ${activeFilterCount} aktiv` : ''}`}
      >
        <i className="fas fa-sliders-h" aria-hidden="true"></i>
        {activeFilterCount > 0 && (
          <span className="filter-badge" aria-hidden="true">{activeFilterCount}</span>
        )}
      </button>

      {/* Auto-Suggest Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <ul
          id="search-suggestions"
          className="search-suggestions-dropdown"
          ref={suggestionsRef}
          role="listbox"
          aria-label="Suchvorschläge"
        >
          {suggestions.map((stock, index) => (
            <li
              key={stock.id}
              id={`suggestion-${stock.id}`}
              className={`suggestion-item${index === activeSuggestionIndex ? ' active' : ''}`}
              role="option"
              aria-selected={index === activeSuggestionIndex}
              onClick={() => onSuggestionSelect(stock)}
            >
              <div className="suggestion-info">
                <span className="suggestion-name">{stock.name}</span>
                {stock.ticker && (
                  <span className="suggestion-ticker">{stock.ticker}</span>
                )}
                {stock.sector && (
                  <span className="suggestion-sector">{stock.sector}</span>
                )}
              </div>
              <i className="fas fa-chevron-right suggestion-arrow" aria-hidden="true"></i>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchBar;
